import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { DialogContent, DialogTitle } from '@mui/material';
import { useState } from 'react';

import { PlanGroup } from '@d-id/types';

import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { Billing } from '@/services/api';
import { useAuth } from '@/services/auth';
import { StyleCollection } from '@/utils/types';

import Button from '@/components/Buttons/Button';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import Close from '../../images/CloseIcon.svg';
import Confetti from '../../images/Confetti.png';
import ErrorIcon from '../../images/Error.svg';
import PartyPopper from '../../images/PartyPopper.svg';
import useGetNewPlan from '../hooks/useGetNewPlan';

interface Props {
    onClose?: () => void;
    isNewSubscription?: boolean;
    requestedPlan?: PlanGroup;
    requestedBilling?: Billing;
    requestedPriceId?: string;
    postConfirmationText?: string;
}

const ImageContainer = styled.div`
    display: flex;
    top: 0;
    margin-top: -5rem;
    height: 26rem;
    min-height: 5rem;
    aspect-ratio: 1/1;
    position: absolute;
`;
const Image = styled.div<{ src: string }>`
    background: url(${props => props.src}) no-repeat center center;
    background-size: cover;
    width: 100%;
    height: 100%;
`;

const Loading = styled(Player)`
    height: 25rem;
    width: 70%;
`;

const Header = styled(DialogTitle)`
    padding: 2rem 0 0 0 !important;
    font-size: 1.6rem;
    height: 2rem;
`;

const Title = styled.h1`
    font-style: normal;
    font-weight: 700;
    font-size: 3.4rem;
    text-align: center;
    letter-spacing: -0.02em;
    color: #3c3c3c;
    margin-block: 0;
`;

const TextP = styled.p`
    font-weight: 700;
    font-size: 2rem;
    text-align: center;
    color: #3c3c3c;
    line-height: 2rem;
    margin: 1rem 0 1rem 0;
`;

const ErrorText = styled.p`
    font-weight: 400;
    font-size: 1.4rem;
    font-style: normal;
    line-height: 1.8rem;
    margin: 0;
    text-align: center;
    margin-bottom: 1.4rem;
`;

const BoldText = styled.p`
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 20px;
    margin-bottom: 1rem;
    margin-top: 2.4rem;
`;

const ConfirmButton = styled(Button)`
    width: 23rem;
    height: 4.5rem;
    backdrop-filter: blur(60px);
    border-radius: 8px;
    font-size: 16px;
    text-transform: none;
`;

const styles: StyleCollection = {
    closeIcon: theme => ({
        marginLeft: 'auto',
        color: theme.colors.primary,
        margin: `30px 10px 0 auto`,
    }),
    contentStyle: {
        flex: 1,
        overflow: 'visible',
        alignItems: 'center',
        width: '53rem',
        height: '32rem',
        display: 'flex',
    },
};

const paperProps = {
    success: {
        style: {
            overflow: 'visible',
            borderRadius: '20px',
            background: `url(${Confetti}) no-repeat center center`,
            backgroundColor: 'white',
            width: '53rem',
            paddingBottom: '3rem',
        },
    },
    default: {
        style: {
            borderRadius: '20px',
            width: '53rem',
            height: '32rem',
            paddingBottom: '4rem',
        },
    },
};

export default function ({ onClose, isNewSubscription, requestedPlan, requestedBilling, requestedPriceId, postConfirmationText }: Props) {
    const { user } = useAuth();
    const { isError } = useGetNewPlan(requestedPriceId, requestedPlan, requestedBilling);

    const planUpdated = [user?.planGroup, user?.productName].includes(requestedPlan as any);
    const intervalUpdated = user?.billingInterval === requestedBilling;
    const updated = isNewSubscription ? planUpdated : planUpdated && intervalUpdated;
    const isLoading = !isError && !updated;
    const [isPostConfirmation, setIsPostConfirmation] = useState(false);

    if (isLoading) {
        return (
            <BootstrapDialog onClose={onClose} open width="53rem" contentstyle={styles.contentStyle} PaperProps={paperProps.default}>
                <DialogContent>
                    <Loading src={LoadingAnimation} autoplay loop />
                    <TextP>Getting your new plan ready</TextP>
                </DialogContent>
            </BootstrapDialog>
        );
    } else if (isError) {
        return (
            <BootstrapDialog onClose={onClose} open width="53rem" contentstyle={styles.contentStyle} PaperProps={paperProps.default}>
                <Header id="post-checkout-modal">
                    <IconButton ariaLabel="close" onClick={onClose} sx={styles.closeIcon}>
                        <img src={Close} />
                    </IconButton>
                </Header>
                <DialogContent>
                    <img src={ErrorIcon} />
                    <BoldText>Verifying your payment</BoldText>
                    <ErrorText>
                        Your new plan will update once <br /> verification is complete
                    </ErrorText>
                    <br />
                    <ConfirmButton color="secondary" variant="contained" onClick={onClose}>
                        I understand
                    </ConfirmButton>
                </DialogContent>
            </BootstrapDialog>
        );
    }

    const renderButtonText = () => {
        return isPostConfirmation && postConfirmationText ? postConfirmationText : 'Continue';
    };

    return (
        <BootstrapDialog onClose={onClose} open width="53rem" contentstyle={styles.contentStyle} PaperProps={paperProps.success}>
            <Header id="post-checkout-modal">
                <IconButton ariaLabel="close" onClick={onClose} sx={styles.closeIcon}>
                    <img src={Close} />
                </IconButton>
            </Header>
            <DialogContent style={{ marginTop: '17rem' }}>
                <ImageContainer>
                    <Image src={PartyPopper} />
                </ImageContainer>
                <Title>Congrats {user?.given_name}!</Title>
                <TextP>Your plan has been successfully upgraded</TextP>
                <br />
                <ConfirmButton
                    color="secondary"
                    onClick={() => {
                        onClose?.();
                        setIsPostConfirmation(true);
                    }}
                    disabled={isPostConfirmation}>
                    {renderButtonText()}
                </ConfirmButton>
            </DialogContent>
        </BootstrapDialog>
    );
}
